import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ScanLine, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { api } from "../lib/api";
import LanguageToggle from "../components/LanguageToggle";

export default function StaffLogin() {
    const { t } = useT();
    const navigate = useNavigate();
    const [pin, setPin] = useState("");
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!pin.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post("/auth/staff/login", { pin: pin.trim() });
            localStorage.setItem("sc_staff_token", data.access_token);
            localStorage.setItem("sc_staff_name", data.name);
            navigate("/staff", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid PIN.");
            setPin("");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[100dvh] text-ink-primary flex items-center justify-center px-6 relative" data-testid="staff-login-page">
            <div className="absolute top-6 right-6"><LanguageToggle /></div>
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-[radial-gradient(50%_40%_at_50%_30%,rgba(212,175,55,0.10),transparent_70%)]" />
            </div>

            <motion.form
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                onSubmit={onSubmit}
                className="relative w-full max-w-md p-10 rounded-sm glass-strong gold-border-anim"
                data-testid="staff-login-form"
            >
                <div className="flex items-center gap-3 mb-2">
                    <ScanLine size={16} className="text-gold" />
                    <span className="label-eyebrow !text-gold">{t("staff.eyebrow")}</span>
                </div>
                <h1 className="font-serif font-medium text-3xl">{t("staff.login_title")}</h1>
                <p className="text-ink-secondary text-sm mt-2">{t("staff.login_sub")}</p>

                <div className="mt-8">
                    <label className="label-eyebrow block mb-2">{t("staff.pin_label")}</label>
                    <input
                        type="password"
                        inputMode="numeric"
                        autoFocus
                        data-testid="staff-pin-input"
                        className="input-line tracking-[0.6em] text-2xl"
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
                        maxLength={12}
                    />
                </div>

                <button
                    type="submit"
                    disabled={busy || pin.length < 4}
                    data-testid="staff-login-submit"
                    className="mt-10 w-full inline-flex items-center justify-center gap-3 px-10 py-3.5 bg-gold text-black rounded-sm font-medium tracking-wide hover:bg-gold-bright transition-all disabled:opacity-40"
                >
                    {busy ? <Loader2 size={18} className="animate-spin" /> : t("staff.login_cta")}
                </button>
            </motion.form>
        </div>
    );
}
